import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../../lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, Image, Eye, EyeOff, Upload } from 'lucide-react';
import { uploadToR2 } from '../../lib/r2Upload';
import { ImagePreview } from '../../components/ImagePreview';

export default function AdminBanners() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    imageUrl: '',
    linkUrl: '',
    isActive: true
  });

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    // Real-time listener for banners
    const q = query(collection(db, 'banners'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const bannersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBanners(bannersData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching banners:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      imageUrl: '',
      linkUrl: '',
      isActive: true
    });
    setEditingBanner(null);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      imageUrl: banner.imageUrl || '',
      linkUrl: banner.linkUrl || '',
      isActive: banner.isActive !== false
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadToR2(file, 'banners');
      
      if (result.success) {
        setFormData({ ...formData, imageUrl: result.url });
        toast.success('Şəkil yükləndi');
      } else {
        toast.error(`Yükləmə xətası: ${result.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Şəkil yükləmə xətası');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.imageUrl) {
      toast.error('Başlıq və şəkil tələb olunur');
      return;
    }

    try {
      const bannerData = {
        title: formData.title,
        subtitle: formData.subtitle,
        imageUrl: formData.imageUrl,
        linkUrl: formData.linkUrl,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString()
      };

      if (editingBanner) {
        await updateDoc(doc(db, 'banners', editingBanner.id), bannerData);
        toast.success('Banner yeniləndi');
      } else {
        bannerData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'banners'), bannerData);
        toast.success('Banner əlavə edildi');
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Xəta baş verdi');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Bu banneri silmək istədiyinizə əminsiniz?')) return;

    try {
      await deleteDoc(doc(db, 'banners', bannerId));
      toast.success('Banner silindi');
    } catch (error) {
      console.error('Error deleting banner:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        toast.error('Silmə icazəsi yoxdur. Firebase rules-u yoxlayın.');
      } else {
        toast.error(`Xəta: ${error.message}`);
      }
    }
  };

  const toggleActive = async (banner) => {
    try {
      const newStatus = !banner.isActive;
      await updateDoc(doc(db, 'banners', banner.id), { isActive: newStatus });
      toast.success(newStatus ? 'Banner aktivləşdirildi' : 'Banner deaktiv edildi');
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast.error('Xəta baş verdi');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-20" data-testid="admin-banners-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Bannerlər
              </h1>
              <p className="text-gray-400 text-sm">{banners.length} banner</p>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-banner-btn">
                <Plus className="w-5 h-5 mr-2" />
                Yeni Banner
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-dark border-gray-700 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">
                  {editingBanner ? 'Banneri Redaktə Et' : 'Yeni Banner Əlavə Et'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label className="text-gray-400 font-medium">Başlıq *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-black/50 border-gray-700 text-white mt-1"
                    placeholder="Yeni oyun tezliklə!"
                    data-testid="banner-title-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-400 font-medium">Alt Başlıq</Label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="bg-black/50 border-gray-700 text-white mt-1"
                    placeholder="Qorxunun yeni səviyyəsini yaşa!"
                  />
                </div>

                {/* Image URL only */}
                <div>
                  <Label className="text-gray-400 font-medium">Şəkil URL *</Label>
                  <div className="flex gap-3 mt-2 items-start">
                    <Input
                      value={formData.imageUrl}
                      onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                      className="bg-black/50 border-gray-700 text-white flex-1"
                      placeholder="https://... və ya /oyunlar/xxx.jpeg"
                      data-testid="banner-image-input"
                    />
                    {formData.imageUrl && (
                      <ImagePreview url={formData.imageUrl} alt="Banner" size="md" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Şəkil URL-ini yapışdırın. Böyütmək üçün preview-a klik edin.</p>
                </div>

                <div>
                  <Label className="text-gray-400 font-medium">Link URL (İstəyə bağlı)</Label>
                  <Input
                    value={formData.linkUrl}
                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                    className="bg-black/50 border-gray-700 text-white mt-1"
                    placeholder="/games/vampir"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label className="text-gray-400 font-medium">Aktiv</Label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-gray-700"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                  >
                    Ləğv Et
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 btn-primary"
                    data-testid="save-banner-btn"
                  >
                    {editingBanner ? 'Yenilə' : 'Əlavə Et'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Banners List */}
        {banners.length === 0 ? (
          <div className="text-center py-16">
            <Image className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">Hələ banner yoxdur</h3>
            <p className="text-gray-400">İlk bannerinizi əlavə edin</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {banners.map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`glass-dark rounded-xl overflow-hidden border ${banner.isActive ? 'border-green-500/30' : 'border-gray-700'}`}
                data-testid={`admin-banner-${banner.id}`}
              >
                {/* Image */}
                <div className="h-40 bg-black/50">
                  {banner.imageUrl ? (
                    <img
                      src={banner.imageUrl}
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-700" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-white">{banner.title}</h3>
                      {banner.subtitle && (
                        <p className="text-gray-400 text-sm">{banner.subtitle}</p>
                      )}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-bold ${banner.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {banner.isActive ? 'Aktiv' : 'Deaktiv'}
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(banner)}
                      className="text-gray-400 hover:text-white hover:bg-white/10"
                    >
                      {banner.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(banner)}
                      className="text-gray-400 hover:text-white hover:bg-white/10"
                      data-testid={`edit-banner-${banner.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(banner.id)}
                      className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-banner-${banner.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
